import { apiClient } from './client';

export interface GameSessionStats {
  totalSessions: number;
  todaySessions: number;
  weekSessions: number;
  monthSessions: number;
  dau: number;
  wau: number;
  bestScoreAll: number;
  flaggedCount: number;
}

export interface DuelStats {
  totalCompleted: number;
  todayCompleted: number;
  weekCompleted: number;
  monthCompleted: number;
  dau: number;
  activeMatches: number;
  waitingMatches: number;
}

export interface DuelMatchRow {
  id: string;
  status: 'waiting' | 'active' | 'completed' | 'expired';
  playerA: string;
  playerB: string | null;
  topicId: number | null;
  createdAt: string;
  matchedAt: string | null;
  expiresAt: string | null;
}

export interface EloDistributionPoint {
  bucket: number;
  count: number;
}

export interface GamesOverview {
  daily: {
    totalChallenges: number;
    totalAttempts: number;
    todayAttempts: number;
    weekAttempts: number;
    monthAttempts: number;
    dau: number;
    wau: number;
    playersWithStreak: number;
    bestStreak: number;
  };
  time_attack: GameSessionStats | null;
  survival: GameSessionStats | null;
  duel: DuelStats | null;
}

export interface UsagePoint {
  date: string;
  count: number;
}

export interface CheaterRow {
  id: string;
  user_id: string;
  score: number;
  duration_ms?: number | null;
  used_revive?: boolean;
  finished_at: string | null;
  status: string;
}

export interface GameLimits {
  duel_free_daily: number;
  time_attack_free_daily: number;
  survival_free_daily: number;
  daily_freeze_per_week: number;
  daily_replay_window_days: number;
  /** Бесплатных AI-разборов вопроса в сутки (сутки — по Ташкенту). */
  ai_explain_free_daily: number;
}

// ─── Спарринг (ADR 0018) ───────────────────────────────────────────────────

export interface SparringSettings {
  enabled: boolean;
  search_window_ms: number;
  rated_cap_per_day: number;
  backfill_enabled: boolean;
  backfill_after_minutes: number;
  backfill_batch_size: number;
}

export interface SparringProfile {
  id: string;
  code: string;
  title: string;
  elo_rating: number;
  accuracy: number;
  tricky_penalty: number;
  min_answer_ms: number;
  max_answer_ms: number;
  weight: number;
  is_active: boolean;
}

export interface SparringPartnerRow {
  id: string;
  gamerTag: string | null;
  profileId: string | null;
}

export interface SparringProfileStats {
  profileId: string;
  code: string;
  title: string;
  eloRating: number;
  partners: number;
  matches: number;
  userWins: number;
  userLosses: number;
  draws: number;
  userWinRate: number;
  avgUserScore: number;
  avgPartnerScore: number;
}

export interface LeaderboardRow {
  rank: number;
  userId: string;
  gamerTag: string;
  score: number;
}

export const gamesAdminApi = {
  async overview(includeSparring = false): Promise<GamesOverview> {
    const res = await apiClient.get<GamesOverview>('/admin/games/overview', {
      params: { includeSparring },
    });
    return res.data;
  },

  async gameLimits(): Promise<GameLimits> {
    const res = await apiClient.get<GameLimits>('/admin/games/limits');
    return res.data;
  },

  async updateGameLimits(patch: Partial<GameLimits>): Promise<GameLimits> {
    const res = await apiClient.patch<GameLimits>('/admin/games/limits', patch);
    return res.data;
  },

  async sparringSettings(): Promise<SparringSettings> {
    const res = await apiClient.get<SparringSettings>(
      '/admin/games/sparring/settings',
    );
    return res.data;
  },

  async updateSparringSettings(
    patch: Partial<SparringSettings>,
  ): Promise<SparringSettings> {
    const res = await apiClient.patch<SparringSettings>(
      '/admin/games/sparring/settings',
      patch,
    );
    return res.data;
  },

  async sparringProfiles(): Promise<SparringProfile[]> {
    const res = await apiClient.get<SparringProfile[]>(
      '/admin/games/sparring/profiles',
    );
    return res.data;
  },

  async updateSparringProfile(
    id: string,
    patch: Partial<SparringProfile>,
  ): Promise<SparringProfile> {
    const res = await apiClient.patch<SparringProfile>(
      `/admin/games/sparring/profiles/${id}`,
      patch,
    );
    return res.data;
  },

  async sparringPartners(profileId?: string): Promise<SparringPartnerRow[]> {
    const res = await apiClient.get<SparringPartnerRow[]>(
      '/admin/games/sparring/partners',
      { params: profileId ? { profileId } : undefined },
    );
    return res.data;
  },

  async addSparringPartner(
    profileId: string,
    name: string,
  ): Promise<SparringPartnerRow> {
    const res = await apiClient.post<SparringPartnerRow>(
      '/admin/games/sparring/partners',
      { profileId, name },
    );
    return res.data;
  },

  async retireSparringPartner(id: string): Promise<void> {
    await apiClient.delete(`/admin/games/sparring/partners/${id}`);
  },

  async sparringStats(): Promise<SparringProfileStats[]> {
    const res = await apiClient.get<SparringProfileStats[]>(
      '/admin/games/sparring/stats',
    );
    return res.data;
  },

  async refreshLeaderboards(): Promise<void> {
    await apiClient.post('/admin/games/leaderboard/refresh');
  },

  async duelLeaderboard(
    period: 'week' | 'all' = 'all',
  ): Promise<{ rows: LeaderboardRow[] }> {
    const res = await apiClient.get<{ rows: LeaderboardRow[] }>(
      '/admin/games/leaderboard',
      { params: { period } },
    );
    return res.data;
  },

  async usage(
    game: 'daily' | 'time_attack' | 'survival',
    days = 30,
  ): Promise<UsagePoint[]> {
    const res = await apiClient.get<UsagePoint[]>('/admin/games/usage', {
      params: { game, days },
    });
    return res.data;
  },

  async cheaters(
    game: 'time_attack' | 'survival',
    limit = 50,
  ): Promise<CheaterRow[]> {
    const res = await apiClient.get<CheaterRow[]>('/admin/games/cheaters', {
      params: { game, limit },
    });
    return res.data;
  },

  async duelsActive(limit = 50): Promise<DuelMatchRow[]> {
    const res = await apiClient.get<DuelMatchRow[]>('/admin/games/duels/active', {
      params: { limit },
    });
    return res.data;
  },

  async eloDistribution(): Promise<EloDistributionPoint[]> {
    const res = await apiClient.get<EloDistributionPoint[]>(
      '/admin/games/elo-distribution',
    );
    return res.data;
  },
};
